import deepCopy from "@utils/deepCopy";
import { rmReverseDuplicates } from "@utils/helper/utils";
import set from "lodash/set";
import { captureException } from "../Multiplayer/hooks/useLoadingExceptions";
import * as jsondiffpatch from "jsondiffpatch";

class FigmaStates {
    figmaUpdatedJson = {};
    figmaUpdatedJsonIds = {};
    tappableIds = {};
    subscribeOnes = {
        submit: false,
        timeout: false,
    };
    attemptComponent = null;
    parent_variables = [];
    figmaNonUpdatedJson = {};
    graphOutput = null;
    showComponents = [];
    disableComponents = [];
    isSubjectiveEvaluation = false;
    subjectiveEvaluationCallback = null;
    showAudioToast = false;
    all_variables = [];
    isDisabled = false;
    isSubmitted = false;
    isGame = false;
    gameIndex = null;
    feedback_area = undefined;
    is_multiplayer = false;
    time_store = {};
    CAPTURE_THRESHOLD = 1500;
    functions = {};
    deltaHistory = [];

    constructor() {
        this.prevSubscribeOnes = deepCopy(this.subscribeOnes);
        this.attemptComponent = null;
        this.parent_variables = [];
        this.all_variables = [];
    }

    getJson(index) {
        return this.figmaUpdatedJson[index];
    }

    setFirstJson(newJson, index) {
        // const json = JSON.parse(JSON.stringify(convertCircularToNonCircular(newJson)));
        this.figmaUpdatedJson[index] = newJson;
    }

    setJson(json, index) {
        if (this.isSubjectiveEvaluation && this.subjectiveEvaluationCallback) {
            this.subjectiveEvaluationCallback(json);
        }
        this.figmaUpdatedJson[index] = json;
    }

    setSubjectiveEvaluation(val, callback) {
        this.isSubjectiveEvaluation = val;
        this.subjectiveEvaluationCallback = callback;
    }

    setJsonPathForIds(index, id, path) {
        this.figmaUpdatedJsonIds[index] = {
            ...this.figmaUpdatedJsonIds[index],
            [id]: path,
        };
    }

    getJsonPathForIds(index, id) {
        return this.figmaUpdatedJsonIds[index]?.[id];
    }

    setTappableIds(index, id, data) {
        this.tappableIds[index] = {
            ...this.tappableIds[index],
            [id]: {
                ...this.tappableIds[index]?.[id],
                [data.type]: { ...data },
            },
        };
    }

    getTappableIds(index, id) {
        return this.tappableIds[index]?.[id];
    }

    updateParentVariables(parentVariables) {
        this.parent_variables = parentVariables;
    }

    getParentVariables() {
        return this.parent_variables;
    }

    getComponentVariables() {
        return this.all_variables;
    }

    setNonUpdatedJson(json, index) {
        const updatedJson =
            index == "question_audios" ? rmReverseDuplicates(json) : json;
        this.figmaNonUpdatedJson[index] = deepCopy(updatedJson);
    }

    resetNonUpdatedJson() {
        Object.keys((i) => (this.figmaNonUpdatedJson[i] = []));
    }

    getNonUpdatedJson(index) {
        return deepCopy(this.figmaNonUpdatedJson[index]);
    }

    async updateSubmissionDeltaHistory(key, json) {
        try {
            const nonjson = JSON.parse(
                JSON.stringify(this.figmaNonUpdatedJson[key] ?? {}),
            );
            const patchJson =
                this.deltaHistory?.length > 0
                    ? this.deltaHistory.reduce(
                          (acc, curr) => jsondiffpatch.patch(acc, curr.delta),
                          nonjson,
                      )
                    : nonjson;
            this.deltaHistory.push({
                time: Date.now(),
                order: this.deltaHistory.length + 1,
                delta: jsondiffpatch.diff(
                    patchJson,
                    JSON.parse(JSON.stringify(json ?? {})),
                ),
            });
        } catch (e) {
            // console.error("Failed to update submission delta history:", e);
        }
    }

    updateValue(key, path, value) {
        const json = this.figmaUpdatedJson[key];
        set(json, path, value);
        this.figmaUpdatedJson[key] = json;
        this.updateSubmissionDeltaHistory(key, json);
    }
    insertVariable(name, value) {
        const index = this.all_variables.findIndex((v) => v.name === name);
        if (index !== -1) {
            this.all_variables[index].value = value;
        } else {
            this.all_variables.push({ name, value });
        }
    }
    updateIsGame(isGame, gameIndex) {
        this.isGame = isGame;
        this.gameIndex = gameIndex;
    }

    startCapturingTime(key, other_info) {
        this.time_store[key] = { start_time: Date.now(), data: other_info };
    }

    stopCapturingTime(key) {
        if (key && this.time_store[key]) {
            this.time_store[key].end_time = Date.now();
            this.time_store[key].duration =
                this.time_store[key].end_time - this.time_store[key].start_time;
            // console.log("Time taken for " + key, this.time_store[key].duration);

            if (this.time_store[key].duration > this.CAPTURE_THRESHOLD) {
                captureException(
                    new Error(
                        `Failed to render ${key} within ${this.CAPTURE_THRESHOLD}ms. Took ${this.time_store[key].duration}ms`,
                    ),
                    {
                        extra: {
                            key: key,
                            ...this.time_store[key],
                        },
                    },
                );
            }
            delete this.time_store[key];
        }
    }

    reset() {
        this.figmaUpdatedJson = {};
        this.subscribeOnes = deepCopy(this.prevSubscribeOnes);
        this.attemptComponent = null;
        this.parent_variables = [];
        this.isSubjectiveEvaluation = false;
        this.subjectiveEvaluationCallback = null;
        this.showAudioToast = false;
        this.all_variables = [];
        this.isDisabled = false;
        this.isSubmitted = false;
        this.isGame = false;
        this.is_multiplayer = false;
        this.time_store = {};
        this.functions = {};
        // this.figmaNonUpdatedJson = {};
        this.deltaHistory = [];
    }
}

export const FigmaJsonStates = new FigmaStates();
// FigmaJsonState.
// export default FigmaState;
